import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, Loader2, Sparkles, X } from 'lucide-react';
import { adsApi } from '@/api/client';
import { keywordReportAdProductsQueryParam } from '@/api/reportAdProducts';
import type {
  KeywordRecommendationAction,
  KeywordRecommendationsData,
} from '@/types/keywordRecommendations';
import { KeywordNegativeApplyModal } from '@/components/KeywordNegativeApplyModal';
import { KeywordApplyConfirmModal } from '@/components/recommendations/KeywordApplyConfirmModal';
import { KeywordRecommendationsTable } from '@/components/recommendations/KeywordRecommendationsTable';
import { SearchTermRecommendationsTable } from '@/components/recommendations/SearchTermRecommendationsTable';
import { RecommendationThresholdsBanner } from '@/components/recommendations/RecommendationThresholdsBanner';
import { SyncReportsButton } from '@/components/SyncReportsButton';
import { useKeywordRecommendationActions } from '@/hooks/useKeywordRecommendationActions';
import {
  buildApplyAction,
  buildSearchTermApplyAction,
} from '@/api/keywordRecommendationApply';
import type { RecommendationSortField, RecommendationSortOrder } from '@/api/recommendationUtils';
import type { DateRange } from '@/api/reportDateRanges';

function parsePayload(data: unknown): KeywordRecommendationsData {
  if (!data || typeof data !== 'object') return {};
  return data as KeywordRecommendationsData;
}

export function KeywordRecommendationsTab({
  profileId,
  range,
  rangeOk,
}: {
  profileId: string;
  range: DateRange;
  rangeOk: boolean;
}) {
  const [searchParams, setSearchParams] = useSearchParams();
  const campaignIdFilter = searchParams.get('campaignId') ?? undefined;

  const invalidRange = !rangeOk;

  const [keywordSearch, setKeywordSearch] = useState('');
  const [keywordSortBy, setKeywordSortBy] = useState<RecommendationSortField>('cost');
  const [keywordSortOrder, setKeywordSortOrder] = useState<RecommendationSortOrder>('desc');

  const [searchTermSearch, setSearchTermSearch] = useState('');
  const [searchTermActionFilter, setSearchTermActionFilter] = useState<
    KeywordRecommendationAction | 'ALL'
  >('ALL');
  const [searchTermSortBy, setSearchTermSortBy] = useState<RecommendationSortField>('cost');
  const [searchTermSortOrder, setSearchTermSortOrder] = useState<RecommendationSortOrder>('desc');

  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: [
      'keyword-recommendations',
      profileId,
      range.startDate,
      range.endDate,
      campaignIdFilter,
    ],
    queryFn: async () => {
      const res = await adsApi.getKeywordRecommendations(profileId, {
        startDate: range.startDate,
        endDate: range.endDate,
        adProducts: keywordReportAdProductsQueryParam(),
        campaignId: campaignIdFilter,
      });
      return parsePayload(res.data);
    },
    enabled: !!profileId && !invalidRange,
  });

  const apiKeywords = data?.keywords ?? [];
  const apiSearchTerms = data?.searchTerms ?? [];

  const {
    keywords,
    searchTerms,
    selected,
    bidOverrides,
    pendingApply,
    pendingNegativeRow,
    applying,
    applyMutation,
    toggleSelect,
    handleSelectAll,
    handleBidChange,
    handleDismiss,
    handleBulkApply,
    handleBulkDismiss,
    clearSelection,
    setPendingApply,
    setPendingNegativeRow,
  } = useKeywordRecommendationActions({
    profileId,
    apiKeywords,
    apiSearchTerms,
  });

  const thresholds = data?.thresholds;

  const clearCampaignFilter = () => {
    const next = new URLSearchParams(searchParams);
    next.delete('campaignId');
    setSearchParams(next, { replace: true });
  };

  return (
    <div id="keyword-optimization" className="scroll-mt-4 space-y-6">
      <h2 className="flex items-center gap-2 text-lg font-semibold text-foreground">
        <Sparkles className="h-5 w-5 text-brand" />
        Keyword optimization
      </h2>

      {campaignIdFilter ? (
        <div className="flex items-center gap-2 rounded-xl border border-brand/20 bg-brand-subtle px-3 py-2 text-sm text-brand">
          <span>
            Filtered to campaign <strong>{campaignIdFilter}</strong>
          </span>
          <button
            type="button"
            onClick={clearCampaignFilter}
            className="ml-auto inline-flex items-center gap-1 text-brand hover:underline"
          >
            <X className="h-3.5 w-3.5" />
            Clear filter
          </button>
        </div>
      ) : null}

      {thresholds ? (
        <RecommendationThresholdsBanner thresholds={thresholds} eligibleCount={data?.eligibleCount} />
      ) : null}

      {invalidRange ? (
        <p className="rounded-xl border border-warning/20 bg-warning-subtle px-3 py-2 text-sm text-warning-ink">
          Select a valid date range.
        </p>
      ) : null}

      {isLoading ? (
        <div className="flex items-center justify-center gap-2 py-12 text-foreground-muted">
          <Loader2 className="h-5 w-5 animate-spin" />
          Loading recommendations…
        </div>
      ) : isError ? (
        <div className="space-y-3 py-12 text-center">
          <AlertTriangle className="mx-auto h-8 w-8 text-warning" />
          <p className="text-sm text-foreground-muted">{(error as Error)?.message || 'Failed to load'}</p>
          <button
            type="button"
            onClick={() => void refetch()}
            className="text-sm text-brand hover:underline"
          >
            Retry
          </button>
        </div>
      ) : data?.noData && !keywords.length && !searchTerms.length ? (
        <div className="rounded-xl border border-warning/20 bg-warning-subtle px-4 py-6 text-center">
          <AlertTriangle className="mx-auto mb-2 h-8 w-8 text-warning" />
          <p className="text-sm font-medium text-warning-ink">No keyword report data for this range</p>
          <p className="mb-4 mt-1 text-sm text-warning-ink">
            Sync keyword and search term reports first, then refresh this view.
          </p>
          <SyncReportsButton
            profileId={profileId}
            className="rounded-lg bg-brand px-4 py-2 text-sm font-medium text-white"
          />
        </div>
      ) : (
        <div className="space-y-6">
          <p className="text-sm text-foreground-muted">
            {data?.eligibleCount ?? 0} eligible keywords · {keywords.length} keyword recommendations ·{' '}
            {searchTerms.length} search term suggestions
          </p>

          <KeywordRecommendationsTable
            profileId={profileId}
            rows={keywords}
            selected={selected}
            bidOverrides={bidOverrides}
            applying={applying}
            searchQuery={keywordSearch}
            sortBy={keywordSortBy}
            sortOrder={keywordSortOrder}
            onSearchChange={setKeywordSearch}
            onSortByChange={setKeywordSortBy}
            onSortOrderChange={setKeywordSortOrder}
            onToggleSelect={toggleSelect}
            onSelectAll={handleSelectAll}
            onBidChange={handleBidChange}
            onApply={(action) => setPendingApply([action])}
            onDismiss={handleDismiss}
            onAddNegative={(row) => setPendingNegativeRow(row)}
            onBulkApply={handleBulkApply}
            onBulkDismiss={handleBulkDismiss}
            onClearSelection={clearSelection}
            buildApplyAction={buildApplyAction}
          />

          <SearchTermRecommendationsTable
            profileId={profileId}
            rows={searchTerms}
            selected={selected}
            bidOverrides={bidOverrides}
            applying={applying}
            searchQuery={searchTermSearch}
            actionFilter={searchTermActionFilter}
            sortBy={searchTermSortBy}
            sortOrder={searchTermSortOrder}
            onSearchChange={setSearchTermSearch}
            onActionFilterChange={setSearchTermActionFilter}
            onSortByChange={setSearchTermSortBy}
            onSortOrderChange={setSearchTermSortOrder}
            onToggleSelect={toggleSelect}
            onSelectAll={handleSelectAll}
            onBidChange={handleBidChange}
            onApply={(action) => setPendingApply([action])}
            onDismiss={handleDismiss}
            onBulkApply={handleBulkApply}
            onBulkDismiss={handleBulkDismiss}
            onClearSelection={clearSelection}
            buildApplyAction={buildSearchTermApplyAction}
          />
        </div>
      )}

      {pendingApply ? (
        <KeywordApplyConfirmModal
          actions={pendingApply}
          pending={applyMutation.isPending}
          onConfirm={() => applyMutation.mutate(pendingApply)}
          onCancel={() => setPendingApply(null)}
        />
      ) : null}

      {pendingNegativeRow ? (
        <KeywordNegativeApplyModal
          row={pendingNegativeRow}
          profileId={profileId}
          pending={applyMutation.isPending}
          onConfirm={(actions) => applyMutation.mutate(actions)}
          onCancel={() => setPendingNegativeRow(null)}
        />
      ) : null}
    </div>
  );
}
